/**
 * SF-5126: the company's help desk forwarding address. The company forwards its own support
 * mailbox to it, and only emails that arrive there become tickets.
 *
 *   POST /admin/v1/companies/:id/help-desk/forwarding-address
 *        -> { help_desk_forwarding_address, help_desk_last_inbound_at: null }   the old address stops
 *           working at once, and has had no email yet
 *        409 HELP_DESK_FORWARDING_OFF while ticket emails are not on forwarding
 *
 * The mode itself (`help_desk_email_mode`) is a normal one-field company PATCH. Same pattern as
 * demoDataClient.ts: the data provider unwraps the sf-api `{ success, data }` envelope.
 */
import { useCustomMutation } from '@refinedev/core';
import type { Company } from '../list';
import { COMPANIES_API_BASE } from './companyDeleteClient';

export const NO_ADDRESS_RETURNED_TEXT =
  'The new address did not come back. Refresh the page to see it.';

export function newForwardingAddressArgs(companyId: string) {
  return {
    url: `/${COMPANIES_API_BASE}/${encodeURIComponent(companyId)}/help-desk/forwarding-address`,
    method: 'post' as const,
    values: {},
    errorNotification: false as const,
    successNotification: false as const,
  };
}

/**
 * Makes a new forwarding address for a company and resolves with the company field it changed,
 * for the page's saver (`send`) to write into that company's cache.
 */
export function useNewForwardingAddress() {
  const { mutateAsync } = useCustomMutation<{ help_desk_forwarding_address?: unknown }>();
  return async (companyId: string): Promise<Partial<Company>> => {
    const { data } = await mutateAsync(newForwardingAddressArgs(companyId));
    const address = data?.help_desk_forwarding_address;
    if (typeof address !== 'string' || !address) throw new Error(NO_ADDRESS_RETURNED_TEXT);
    // SF-5127: the new address has had no email yet (sf-api clears it with the new address), so the
    // page never shows the old address's last email next to the new one, even if its re-read fails.
    return { help_desk_forwarding_address: address, help_desk_last_inbound_at: null };
  };
}
